import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { ArrowLeft, Check, ShieldCheck, Save, AlertTriangle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/stores/toast';
import { useAuthStore } from '@/stores/auth';
import { AnnexTab } from '@/components/psmf/AnnexTab';
import { LogbookWidget } from '@/components/psmf/LogbookWidget';
import {
  emptyPsmf,
  ANNEX_KEYS,
  MAIN_SECTIONS,
  type AnnexData,
  type AnnexKey,
  type Psmf,
  type PsmfLogEntry,
} from '@/components/psmf/types';
import { loadPsmf, savePsmf, newPsmfId } from '@/components/psmf/storage';
import { scanPsmf, type PsmfScanResult, type PsmfFinding } from '@/components/psmf/rules';

type View =
  | { kind: 'cover' }
  | { kind: 'main'; section: typeof MAIN_SECTIONS[number]['key'] }
  | { kind: 'annex'; key: AnnexKey };

export function PsmfBuilderPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const tenantId = user?.tenantId;
  const currentUser = user ? `${user.firstName} ${user.lastName}` : 'unknown';

  const [psmf, setPsmf] = useState<Psmf | null>(null);
  const [view, setView] = useState<View>({ kind: 'cover' });
  const [scan, setScan] = useState<PsmfScanResult | null>(null);

  // Load or initialise
  useEffect(() => {
    if (!tenantId) return;
    if (id && id !== 'new') {
      const existing = loadPsmf(tenantId, id);
      if (existing) {
        setPsmf(existing);
        return;
      }
      toast.error('PSMF not found', `No PSMF with id ${id} in this tenant.`);
      navigate('/psmf');
      return;
    }
    const fresh = emptyPsmf(tenantId, newPsmfId());
    setPsmf(fresh);
  }, [id, tenantId, navigate]);

  const findingsBySection = useMemo(() => {
    if (!scan) return {} as Record<string, PsmfFinding[]>;
    const by: Record<string, PsmfFinding[]> = {};
    for (const f of scan.findings) {
      (by[f.rule.section] ??= []).push(f);
    }
    return by;
  }, [scan]);

  if (!psmf || !tenantId) {
    return <div className="p-6 text-sm text-muted-foreground">Loading PSMF...</div>;
  }

  // --- mutation helpers -------------------------------------------------

  function logChange(field: string, summary: string) {
    const entry: PsmfLogEntry = {
      id: newPsmfId().slice(0, 10),
      timestamp: new Date().toISOString(),
      user: currentUser,
      field,
      summary,
    };
    setPsmf((prev) => {
      if (!prev) return prev;
      const next: Psmf = {
        ...prev,
        updatedAt: new Date().toISOString(),
        cover: { ...prev.cover, lastUpdatedAt: new Date().toISOString().slice(0, 10) },
        logbook: [entry, ...prev.logbook].slice(0, 500),
      };
      return next;
    });
  }

  function updateCover<K extends keyof Psmf['cover']>(key: K, value: Psmf['cover'][K], label?: string) {
    setPsmf((prev) => prev ? { ...prev, cover: { ...prev.cover, [key]: value } } : prev);
    logChange(`Cover.${String(key)}`, label ?? `Set ${String(key)}`);
  }

  function updateMain<S extends keyof Psmf['main']>(
    section: S,
    patch: Partial<Psmf['main'][S]>,
    label: string,
  ) {
    setPsmf((prev) => prev ? { ...prev, main: { ...prev.main, [section]: { ...prev.main[section], ...patch } } } : prev);
    logChange(`Main.${String(section)}`, label);
  }

  const updateAnnex = (key: AnnexKey, updater: (prev: AnnexData[AnnexKey]) => AnnexData[AnnexKey], fieldLabel: string) => {
    setPsmf((prev) => {
      if (!prev) return prev;
      const prevAnnex = prev.annex[key];
      const nextAnnex = updater(prevAnnex) as AnnexData[AnnexKey];
      return { ...prev, annex: { ...prev.annex, [key]: nextAnnex } as AnnexData };
    });
    logChange(`Annex.${key}`, fieldLabel);
  };

  // --- save / compliance ------------------------------------------------

  const saveMutation = useMutation({
    mutationFn: async (p: Psmf) => {
      savePsmf(p);
      return p;
    },
    onSuccess: (p) => {
      toast.success('PSMF saved', `Version ${p.cover.versionNumber} stored locally.`);
      if (id === 'new' || !id) navigate(`/psmf/${p.id}`, { replace: true });
    },
    onError: (err) => toast.error('Save failed', (err as Error).message),
  });

  const checkMutation = useMutation({
    /**
     * Local compliance check. The backend `/api/compliance/scans` endpoint
     * requires a persisted Document; once /api/psmf exists, this will swap
     * to a server-side scan. See rules.ts for the shared keyword list.
     */
    mutationFn: async (p: Psmf) => scanPsmf(p),
    onSuccess: (result) => {
      setScan(result);
      toast.success('Compliance check complete', result.summary);
    },
    onError: (err) => toast.error('Check failed', (err as Error).message),
  });

  const finalise = () => {
    if (!psmf) return;
    setPsmf({ ...psmf, status: 'FINALISED' });
    logChange('Status', 'Finalised PSMF');
  };

  // --- render -----------------------------------------------------------

  const sectionStatus = (section: string): 'pass' | 'fail' | 'unknown' => {
    const fs = findingsBySection[section];
    if (!fs || fs.length === 0) return 'unknown';
    return fs.every((f) => f.status === 'pass') ? 'pass' : 'fail';
  };

  return (
    <div className="space-y-6">
      <div>
        <Button asChild variant="ghost" size="sm" className="-ml-2 mb-2">
          <Link to="/psmf"><ArrowLeft className="mr-2 h-4 w-4" /> PSMFs</Link>
        </Button>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">
              PSMF v{psmf.cover.versionNumber}
              {' '}
              <Badge variant={psmf.status === 'FINALISED' ? 'success' : 'warning'}>{psmf.status}</Badge>
            </h1>
            <p className="text-sm text-muted-foreground">
              {psmf.cover.mahName || 'Unnamed MAH'} — QPPV: {psmf.cover.qppvName || 'not set'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => checkMutation.mutate(psmf)} disabled={checkMutation.isPending}>
              <ShieldCheck className="mr-1.5 h-4 w-4" />
              {checkMutation.isPending ? 'Checking...' : 'Check compliance'}
            </Button>
            <Button onClick={() => saveMutation.mutate(psmf)} disabled={saveMutation.isPending}>
              <Save className="mr-1.5 h-4 w-4" />
              {saveMutation.isPending ? 'Saving...' : 'Save draft'}
            </Button>
            {psmf.status !== 'FINALISED' && (
              <Button variant="secondary" onClick={finalise}>Finalise</Button>
            )}
          </div>
        </div>
      </div>

      {scan && <ScanSummary scan={scan} />}

      <div className="grid gap-6 lg:grid-cols-[220px_1fr]">
        <LeftNav
          view={view}
          onSelect={setView}
          status={sectionStatus}
          annexData={psmf.annex}
        />

        <div className="min-w-0 space-y-4">
          {view.kind === 'cover' && (
            <CoverPanel
              psmf={psmf}
              onCoverChange={updateCover}
              findings={findingsBySection.cover}
              status={sectionStatus('cover')}
            />
          )}
          {view.kind === 'main' && (
            <MainSectionPanel
              sectionKey={view.section}
              psmf={psmf}
              onMainChange={updateMain}
              findings={findingsBySection[`main:${view.section}`]}
              status={sectionStatus(`main:${view.section}`)}
            />
          )}
          {view.kind === 'annex' && (
            <AnnexTab
              annexKey={view.key}
              psmf={psmf}
              onAnnexChange={updateAnnex}
              onFindingStatus={sectionStatus(`annex:${view.key}`)}
            />
          )}
        </div>
      </div>

      <LogbookWidget entries={psmf.logbook} />
    </div>
  );
}

// ---------------------------------------------------------------------
// Scan summary banner
// ---------------------------------------------------------------------

function ScanSummary({ scan }: { scan: PsmfScanResult }) {
  const colour = scan.readinessScore >= 80 ? 'text-emerald-600' : scan.readinessScore >= 50 ? 'text-amber-600' : 'text-destructive';
  return (
    <Card>
      <CardContent className="flex flex-wrap items-center justify-between gap-4 py-4">
        <div>
          <div className="text-xs text-muted-foreground">Readiness</div>
          <div className={`text-3xl font-bold ${colour}`}>{scan.readinessScore}%</div>
        </div>
        <div className="flex-1 min-w-[200px]">
          <div className="text-sm">{scan.summary}</div>
          <div className="mt-1 text-xs text-muted-foreground">
            {scan.passedChecks}/{scan.totalChecks} checks passed, {scan.failedChecks} open
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------
// Left nav
// ---------------------------------------------------------------------

function LeftNav({
  view,
  onSelect,
  status,
  annexData,
}: {
  view: View;
  onSelect: (v: View) => void;
  status: (section: string) => 'pass' | 'fail' | 'unknown';
  annexData: AnnexData;
}) {
  return (
    <nav className="space-y-1 text-sm">
      <NavItem
        active={view.kind === 'cover'}
        onClick={() => onSelect({ kind: 'cover' })}
        status={status('cover')}
        label="Cover page"
      />
      <div className="mt-4 mb-1 text-xs font-medium text-muted-foreground">Main body (II.B.4)</div>
      {MAIN_SECTIONS.map((s) => (
        <NavItem
          key={s.key}
          active={view.kind === 'main' && view.section === s.key}
          onClick={() => onSelect({ kind: 'main', section: s.key })}
          status={status(`main:${s.key}`)}
          label={`${s.ref} ${s.title}`}
        />
      ))}
      <div className="mt-4 mb-1 text-xs font-medium text-muted-foreground">Annex (II.B.6.1)</div>
      {ANNEX_KEYS.map((key) => (
        <NavItem
          key={key}
          active={view.kind === 'annex' && view.key === key}
          onClick={() => onSelect({ kind: 'annex', key })}
          status={status(`annex:${key}`)}
          label={`Annex ${key}${annexData[key].unused ? ' — unused' : ''}`}
        />
      ))}
    </nav>
  );
}

function NavItem({
  active,
  onClick,
  status,
  label,
}: {
  active: boolean;
  onClick: () => void;
  status: 'pass' | 'fail' | 'unknown';
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center justify-between rounded-md px-3 py-1.5 text-left transition-colors ${
        active ? 'bg-primary/10 font-medium text-primary' : 'hover:bg-muted'
      }`}
    >
      <span className="truncate">{label}</span>
      {status === 'pass' && <Check className="h-3.5 w-3.5 text-emerald-600" />}
      {status === 'fail' && <X className="h-3.5 w-3.5 text-destructive" />}
    </button>
  );
}

// ---------------------------------------------------------------------
// Cover panel
// ---------------------------------------------------------------------

function CoverPanel({
  psmf,
  onCoverChange,
  findings,
  status,
}: {
  psmf: Psmf;
  onCoverChange: <K extends keyof Psmf['cover']>(key: K, value: Psmf['cover'][K], label?: string) => void;
  findings: PsmfFinding[] | undefined;
  status: 'pass' | 'fail' | 'unknown';
}) {
  const c = psmf.cover;
  return (
    <div className="space-y-4">
      <FindingsStrip title="Cover page" findings={findings} status={status} />
      <Card>
        <CardHeader><CardTitle className="text-base">Cover page</CardTitle></CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-2">
          <Field label="MAH name">
            <Input value={c.mahName} onChange={(e) => onCoverChange('mahName', e.target.value, 'Set MAH name')} />
          </Field>
          <Field label="Version number">
            <Input value={c.versionNumber} onChange={(e) => onCoverChange('versionNumber', e.target.value, 'Set version')} />
          </Field>
          <Field label="QPPV name" required>
            <Input value={c.qppvName} onChange={(e) => onCoverChange('qppvName', e.target.value, 'Set QPPV name')} />
          </Field>
          <Field label="QPPV email">
            <Input type="email" value={c.qppvEmail} onChange={(e) => onCoverChange('qppvEmail', e.target.value, 'Set QPPV email')} />
          </Field>
          <Field label="QPPV phone (24/7)">
            <Input value={c.qppvPhone} onChange={(e) => onCoverChange('qppvPhone', e.target.value, 'Set QPPV phone')} />
          </Field>
          <Field label="QPPV alternative contact">
            <Input value={c.qppvAltContact} onChange={(e) => onCoverChange('qppvAltContact', e.target.value, 'Set alt contact')} />
          </Field>
          <Field label="EV PSMF reference (Article 57)">
            <Input value={c.evPsmfRef} onChange={(e) => onCoverChange('evPsmfRef', e.target.value, 'Set EV PSMF ref')} placeholder="PSMF-XXXXXXXX" />
          </Field>
          <Field label="Location (EU/EEA physical address)">
            <Input value={c.location} onChange={(e) => onCoverChange('location', e.target.value, 'Set location')} placeholder="e.g. Dublin, Ireland (EU)" />
          </Field>
          <Field label="Date of preparation">
            <Input type="date" value={c.preparedAt} onChange={(e) => onCoverChange('preparedAt', e.target.value, 'Set prep date')} />
          </Field>
          <Field label="Date of last update">
            <Input type="date" value={c.lastUpdatedAt} onChange={(e) => onCoverChange('lastUpdatedAt', e.target.value, 'Set last update')} />
          </Field>
          <Field label="Language">
            <Input value={c.language} onChange={(e) => onCoverChange('language', e.target.value, 'Set language')} />
          </Field>
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------
// Main section panels
// ---------------------------------------------------------------------

function MainSectionPanel({
  sectionKey,
  psmf,
  onMainChange,
  findings,
  status,
}: {
  sectionKey: typeof MAIN_SECTIONS[number]['key'];
  psmf: Psmf;
  onMainChange: <S extends keyof Psmf['main']>(section: S, patch: Partial<Psmf['main'][S]>, label: string) => void;
  findings: PsmfFinding[] | undefined;
  status: 'pass' | 'fail' | 'unknown';
}) {
  const meta = MAIN_SECTIONS.find((s) => s.key === sectionKey)!;
  return (
    <div className="space-y-4">
      <FindingsStrip title={`Section ${meta.ref} — ${meta.title}`} findings={findings} status={status} />
      <Card>
        <CardHeader><CardTitle className="text-base">{meta.ref} — {meta.title}</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {sectionKey === 'qppv' && <QppvForm psmf={psmf} onMainChange={onMainChange} />}
          {sectionKey === 'organisation' && <OrganisationForm psmf={psmf} onMainChange={onMainChange} />}
          {sectionKey === 'sources' && <SourcesForm psmf={psmf} onMainChange={onMainChange} />}
          {sectionKey === 'systems' && <SystemsForm psmf={psmf} onMainChange={onMainChange} />}
          {sectionKey === 'processes' && <ProcessesForm psmf={psmf} onMainChange={onMainChange} />}
          {sectionKey === 'performance' && <PerformanceForm psmf={psmf} onMainChange={onMainChange} />}
          {sectionKey === 'quality' && <QualityForm psmf={psmf} onMainChange={onMainChange} />}
        </CardContent>
      </Card>
    </div>
  );
}

function Field({ label, children, required }: { label: string; children: React.ReactNode; required?: boolean }) {
  return (
    <div>
      <Label>{label}{required && <span className="ml-0.5 text-destructive">*</span>}</Label>
      {children}
    </div>
  );
}

function QppvForm({ psmf, onMainChange }: { psmf: Psmf; onMainChange: <S extends keyof Psmf['main']>(section: S, patch: Partial<Psmf['main'][S]>, label: string) => void }) {
  const q = psmf.main.qppv;
  return (
    <div className="grid gap-3 md:grid-cols-2">
      <Field label="Deputy QPPV name"><Input value={q.deputyName} onChange={(e) => onMainChange('qppv', { deputyName: e.target.value }, 'Set deputy QPPV name')} /></Field>
      <Field label="Deputy QPPV contact"><Input value={q.deputyContact} onChange={(e) => onMainChange('qppv', { deputyContact: e.target.value }, 'Set deputy contact')} /></Field>
      <div className="md:col-span-2">
        <Label>Summary of QPPV delegated activities</Label>
        <Textarea rows={4} value={q.delegationSummary} onChange={(e) => onMainChange('qppv', { delegationSummary: e.target.value }, 'Update delegation summary')} />
        <p className="mt-1 text-xs text-muted-foreground">Detailed table lives in Annex A.</p>
      </div>
      <div className="md:col-span-2">
        <Label>QPPV change-notification triggers</Label>
        <Textarea
          rows={3}
          value={q.notificationTriggers}
          onChange={(e) => onMainChange('qppv', { notificationTriggers: e.target.value }, 'Update notification triggers')}
          placeholder="PSMF updates, CAPA additions, oversight changes, service transfers, product additions, workload changes..."
        />
      </div>
    </div>
  );
}

function OrganisationForm({ psmf, onMainChange }: { psmf: Psmf; onMainChange: <S extends keyof Psmf['main']>(section: S, patch: Partial<Psmf['main'][S]>, label: string) => void }) {
  const o = psmf.main.organisation;
  return (
    <div className="space-y-3">
      <Field label="Organisational chart (narrative)">
        <Textarea rows={4} value={o.chartDescription} onChange={(e) => onMainChange('organisation', { chartDescription: e.target.value }, 'Update org chart')} placeholder="Describe the organisational chart — reference uploaded diagram in Annex B." />
      </Field>
      <Field label="Reporting lines">
        <Textarea rows={3} value={o.reportingLines} onChange={(e) => onMainChange('organisation', { reportingLines: e.target.value }, 'Update reporting lines')} />
      </Field>
      <Field label="Interfaces (Medical Affairs, Regulatory, Quality)">
        <Textarea rows={3} value={o.interfaces} onChange={(e) => onMainChange('organisation', { interfaces: e.target.value }, 'Update interfaces')} />
      </Field>
      <Field label="Sites (locations of PV personnel)">
        <Textarea rows={2} value={o.sites} onChange={(e) => onMainChange('organisation', { sites: e.target.value }, 'Update sites')} />
      </Field>
    </div>
  );
}

function SourcesForm({ psmf, onMainChange }: { psmf: Psmf; onMainChange: <S extends keyof Psmf['main']>(section: S, patch: Partial<Psmf['main'][S]>, label: string) => void }) {
  const s = psmf.main.sources;
  const check = (k: keyof typeof s, label: string) => (
    <label className="flex items-center gap-2 rounded-md border p-2 text-sm">
      <input
        type="checkbox"
        className="h-4 w-4"
        checked={Boolean(s[k])}
        onChange={(e) => onMainChange('sources', { [k]: e.target.checked } as Partial<Psmf['main']['sources']>, `Toggle source — ${label}`)}
      />
      {label}
    </label>
  );
  return (
    <div className="space-y-3">
      <div className="grid gap-2 md:grid-cols-2">
        {check('spontaneous', 'Spontaneous reports')}
        {check('clinicalTrials', 'Clinical trials')}
        {check('literature', 'Literature')}
        {check('regulatoryAuthorities', 'Regulatory authorities')}
        {check('partners', 'Partners / licensees')}
        {check('patientSupportProgrammes', 'Patient support programmes')}
        {check('socialMedia', 'Social media (monitored)')}
      </div>
      <Field label="Notes">
        <Textarea rows={3} value={s.notes} onChange={(e) => onMainChange('sources', { notes: e.target.value }, 'Update sources notes')} />
      </Field>
    </div>
  );
}

function SystemsForm({ psmf, onMainChange }: { psmf: Psmf; onMainChange: <S extends keyof Psmf['main']>(section: S, patch: Partial<Psmf['main'][S]>, label: string) => void }) {
  const s = psmf.main.systems;
  return (
    <div className="grid gap-3 md:grid-cols-2">
      <Field label="Safety database name"><Input value={s.safetyDbName} onChange={(e) => onMainChange('systems', { safetyDbName: e.target.value }, 'Set safety DB name')} placeholder="Argus, ArisGlobal, Veeva Vault Safety..." /></Field>
      <Field label="Version"><Input value={s.safetyDbVersion} onChange={(e) => onMainChange('systems', { safetyDbVersion: e.target.value }, 'Set DB version')} /></Field>
      <div className="md:col-span-2">
        <Field label="Validation status">
          <Textarea rows={2} value={s.validationStatus} onChange={(e) => onMainChange('systems', { validationStatus: e.target.value }, 'Update validation status')} placeholder="GAMP 5 validated — IQ/OQ/PQ complete YYYY-MM-DD." />
        </Field>
      </div>
      <Field label="Fitness for purpose"><Textarea rows={2} value={s.fitnessForPurpose} onChange={(e) => onMainChange('systems', { fitnessForPurpose: e.target.value }, 'Update fitness for purpose')} /></Field>
      <Field label="Change control"><Textarea rows={2} value={s.changeControl} onChange={(e) => onMainChange('systems', { changeControl: e.target.value }, 'Update change control')} /></Field>
      <Field label="Backup and DR"><Textarea rows={2} value={s.backup} onChange={(e) => onMainChange('systems', { backup: e.target.value }, 'Update backup and DR')} /></Field>
    </div>
  );
}

function ProcessesForm({ psmf, onMainChange }: { psmf: Psmf; onMainChange: <S extends keyof Psmf['main']>(section: S, patch: Partial<Psmf['main'][S]>, label: string) => void }) {
  const p = psmf.main.processes;
  const block = (k: keyof typeof p, label: string) => (
    <Field label={label}>
      <Textarea rows={2} value={p[k]} onChange={(e) => onMainChange('processes', { [k]: e.target.value } as Partial<Psmf['main']['processes']>, `Update ${label}`)} />
    </Field>
  );
  return (
    <div className="grid gap-3 md:grid-cols-2">
      {block('icsrProcess', 'ICSR intake, processing, submission')}
      {block('signalDetection', 'Signal detection and management')}
      {block('psurProcess', 'PSUR / PBRER')}
      {block('riskBenefit', 'Risk-benefit monitoring')}
      {block('safetyCommunication', 'Safety communication (DHPC, SmPC)')}
      {block('smpcVariations', 'SmPC / PIL variation implementation')}
    </div>
  );
}

function PerformanceForm({ psmf, onMainChange }: { psmf: Psmf; onMainChange: <S extends keyof Psmf['main']>(section: S, patch: Partial<Psmf['main'][S]>, label: string) => void }) {
  const p = psmf.main.performance;
  return (
    <div className="space-y-3">
      <Field label="KPI summary"><Textarea rows={3} value={p.kpiSummary} onChange={(e) => onMainChange('performance', { kpiSummary: e.target.value }, 'Update KPI summary')} /></Field>
      <Field label="Compliance rate (latest)"><Input value={p.complianceRate} onChange={(e) => onMainChange('performance', { complianceRate: e.target.value }, 'Update compliance rate')} placeholder="e.g. 96.3% on-time expedited reporting" /></Field>
      <Field label="Variation submission tracking"><Textarea rows={2} value={p.variationTracking} onChange={(e) => onMainChange('performance', { variationTracking: e.target.value }, 'Update variation tracking')} /></Field>
      <p className="text-xs text-muted-foreground">Detailed KPI table and 15/90-day charts live in Annex F.</p>
    </div>
  );
}

function QualityForm({ psmf, onMainChange }: { psmf: Psmf; onMainChange: <S extends keyof Psmf['main']>(section: S, patch: Partial<Psmf['main'][S]>, label: string) => void }) {
  const q = psmf.main.quality;
  return (
    <div className="grid gap-3 md:grid-cols-2">
      <Field label="Quality system overview"><Textarea rows={3} value={q.qualitySystem} onChange={(e) => onMainChange('quality', { qualitySystem: e.target.value }, 'Update quality overview')} /></Field>
      <Field label="Training"><Textarea rows={3} value={q.training} onChange={(e) => onMainChange('quality', { training: e.target.value }, 'Update training')} /></Field>
      <Field label="Audit programme"><Textarea rows={3} value={q.auditProgramme} onChange={(e) => onMainChange('quality', { auditProgramme: e.target.value }, 'Update audit programme')} /></Field>
      <Field label="Deviation management"><Textarea rows={3} value={q.deviationManagement} onChange={(e) => onMainChange('quality', { deviationManagement: e.target.value }, 'Update deviation mgmt')} /></Field>
    </div>
  );
}

// ---------------------------------------------------------------------
// Findings strip
// ---------------------------------------------------------------------

function FindingsStrip({
  title,
  findings,
  status,
}: {
  title: string;
  findings: PsmfFinding[] | undefined;
  status: 'pass' | 'fail' | 'unknown';
}) {
  if (!findings || findings.length === 0) return null;
  const failed = findings.filter((f) => f.status === 'fail');
  const passed = findings.filter((f) => f.status === 'pass');

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          {status === 'pass' ? <Check className="h-4 w-4 text-emerald-600" /> : <AlertTriangle className="h-4 w-4 text-amber-600" />}
          {title} — {passed.length}/{findings.length} checks passed
        </CardTitle>
      </CardHeader>
      {failed.length > 0 && (
        <CardContent className="space-y-2">
          {failed.map((f) => (
            <div key={f.rule.id} className="rounded-md border border-destructive/30 bg-destructive/5 p-2 text-sm">
              <div className="flex items-center gap-2">
                <Badge variant={f.rule.severity === 'critical' ? 'destructive' : 'warning'}>{f.rule.severity}</Badge>
                <span className="text-xs font-mono text-muted-foreground">{f.rule.reference}</span>
              </div>
              <div className="mt-1 font-medium">{f.rule.title}</div>
              <div className="text-xs">{f.rule.description}</div>
              <div className="mt-1 text-xs"><span className="font-medium">Recommendation:</span> {f.rule.recommendation}</div>
            </div>
          ))}
          {passed.length > 0 && (
            <div className="text-xs text-muted-foreground">
              Passed: {passed.map((f) => f.rule.id).join(', ')}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}
